import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Tabs } from 'ui/atoms'
import NewButton from 'ui/atoms/NewButton/NewButton'
import NewSwitch from 'ui/atoms/NewSwitch/NewSwitch'
import type { TabItem } from 'ui/atoms/Tabs/Tabs'
import ModuleHeader from 'ui/components/ModuleHeader/ModuleHeader'
import styles from '../IntradayTradingView.module.less'
import type { HourInput, IntradayPrices } from '../IntradayTradingView.types'
import type { DualHeaderPopovers } from '../utils/hooks/useDualHeaderPopovers'
import { IntradayHeaderPopover } from './IntradayHeaderPopover'
import { IntradayPricesDisplay } from './IntradayPricesDisplay'
import { PriceInputSection } from './PriceInputSection'

type IntradayTradingHeaderProps = {
  showOptimizeControls: boolean
  showSubtypeSwitch: boolean
  subtypes: Subtype[]
  activeSubtype: string
  onSubtypeChange: (subtype: string) => void
  popovers: DualHeaderPopovers
  reviewPrices: IntradayPrices | undefined
  hasCompletedOptimization: boolean
  inputPrices: HourInput[]
  priceHourLabels: string[]
  hasInvalidPriceInputs: boolean
  onPriceChange: (id: number, value: string) => void
  onRemoveLastRow: () => void
  onAddRow: () => void
  optimizeButtonLabel: string
  optimizeButtonDisabled: boolean
  optimizeButtonTooltip: string
  optimizationStatusText: string
  showOptimizationIsRunning: boolean
  onOptimize: () => void
  showTabs: boolean
  tabs: TabItem[]
  tabsStorageKey: string
  onTabChange: (id: string) => void
}

export function IntradayTradingHeader({
  showOptimizeControls,
  showSubtypeSwitch,
  subtypes,
  activeSubtype,
  onSubtypeChange,
  popovers,
  reviewPrices,
  hasCompletedOptimization,
  inputPrices,
  priceHourLabels,
  hasInvalidPriceInputs,
  onPriceChange,
  onRemoveLastRow,
  onAddRow,
  optimizeButtonLabel,
  optimizeButtonDisabled,
  optimizeButtonTooltip,
  optimizationStatusText,
  showOptimizationIsRunning,
  onOptimize,
  showTabs,
  tabs,
  tabsStorageKey,
  onTabChange,
}: IntradayTradingHeaderProps): ReactNode {
  const { t } = useTranslation()

  return (
    <>
      <ModuleHeader
        infoHeader={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span>{t(`Intraday Trading`)}</span>
            {showOptimizeControls && showSubtypeSwitch && (
              <div style={{ fontSize: '14px', fontWeight: 'normal' }}>
                <NewSwitch
                  items={subtypes.map((s) => ({ value: s.subtype, label: s.subtype_display_name }))}
                  value={activeSubtype}
                  onClick={onSubtypeChange}
                />
              </div>
            )}
          </div>
        }
        includePropertyFilter={false}
        secondaryChildren={
          showOptimizeControls ? (
            <div className={styles.IntradayTradingView_HeaderPopovers}>
              <IntradayHeaderPopover
                ref={popovers.currentResultPopoverPaperRef}
                anchorEl={popovers.currentResultPopoverAnchorEl}
                onClose={popovers.closeCurrentResultInputPopover}
                title={t('Input used in current result')}
                placement={popovers.currentResultPopoverPlacement}
                horizontalOffset={popovers.currentResultPopoverHorizontalOffset}
                clickAwayExcludeRefs={[popovers.popoverToggleButtonsRef, popovers.nextOptPopoverPaperRef]}
              >
                {reviewPrices ? (
                  <IntradayPricesDisplay prices={reviewPrices} collapsible={false} />
                ) : hasCompletedOptimization ? (
                  <p className={styles.IntradayTradingView_PricesUnavailable}>
                    {t('Input prices for this optimization are unavailable')}
                  </p>
                ) : (
                  <p className={styles.IntradayTradingView_PricesUnavailable}>{t('No completed optimization yet')}</p>
                )}
              </IntradayHeaderPopover>

              <IntradayHeaderPopover
                ref={popovers.nextOptPopoverPaperRef}
                anchorEl={popovers.nextOptimizationInputAnchorEl}
                onClose={popovers.closeNextOptimizationInputPopover}
                title={t('Price input for the next optimization')}
                placement={popovers.nextOptimizationPopoverPlacement}
                horizontalOffset={popovers.nextOptimizationPopoverHorizontalOffset}
                clickAwayExcludeRefs={[popovers.popoverToggleButtonsRef, popovers.currentResultPopoverPaperRef]}
              >
                <PriceInputSection
                  inputPrices={inputPrices}
                  priceHourLabels={priceHourLabels}
                  hasInvalidPriceInputs={hasInvalidPriceInputs}
                  onPriceChange={onPriceChange}
                  onRemoveLastRow={onRemoveLastRow}
                  onAddRow={onAddRow}
                />
              </IntradayHeaderPopover>
            </div>
          ) : undefined
        }
      >
        {showOptimizeControls && (
          <div className={styles.IntradayTradingView_HeaderActions}>
            {/* Tight wrapper for clickaway exclusion — display:contents keeps buttons as direct flex children */}
            <div ref={popovers.popoverToggleButtonsRef} style={{ display: 'contents' }}>
              <NewButton
                variant="outlined"
                label={t('Input used in current result')}
                onClick={popovers.toggleCurrentResultInputPopover}
              />
              <NewButton
                variant="outlined"
                label={t('Price input for the next optimization')}
                onClick={popovers.toggleNextOptimizationInputPopover}
              />
            </div>
            <div className={styles.IntradayTradingView_OptimizeAction}>
              <NewButton
                variant="optimize"
                label={optimizeButtonLabel}
                onClick={onOptimize}
                disabled={optimizeButtonDisabled}
                loading={showOptimizationIsRunning}
                tooltip={{
                  title: optimizeButtonTooltip,
                  placement: 'bottom',
                }}
              />
              <span className={styles.IntradayTradingView_LastOptimizedLabel}>{optimizationStatusText}</span>
            </div>
          </div>
        )}
      </ModuleHeader>
      {showTabs && (
        <div className={styles.IntradayTradingView_Tabs}>
          <Tabs
            items={tabs}
            onChange={(_, id) => {
              onTabChange(id as string)
            }}
            innerLeftPadding
            marginBottom={false}
            uniqueStorageKey={tabsStorageKey}
          />
        </div>
      )}
    </>
  )
}
